import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { LeaderboardRow, PublicStats } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Btn } from "../components/Btn";
import { Leaderboard } from "../components/Leaderboard";
import { Progress } from "../components/Progress";
import { RideCard } from "../components/RideCard";
import { SectionHead } from "../components/SectionHead";
import { Stat } from "../components/Stat";
import { SyncBadge } from "../components/SyncBadge";
import { money } from "../lib/format";

export function Home() {
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [fullWall, setFullWall] = useState<LeaderboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useAuth();

  useEffect(() => {
    api
      .get<PublicStats>("/public/stats")
      .then(setStats)
      .catch((e) => setError(e.message));
  }, []);

  // Signed-in users see full last names via the authenticated pledger wall.
  // Anonymous visitors keep the last-initial view from /public/stats.
  useEffect(() => {
    if (!user) {
      setFullWall(null);
      return;
    }
    let cancelled = false;
    api
      .get<{ pledgers: LeaderboardRow[] }>("/pledgers")
      .then((r) => {
        if (!cancelled) setFullWall(r.pledgers);
      })
      .catch(() => {
        if (!cancelled) setFullWall(null); // fall back to initials
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (error) {
    return <div style={{ padding: 32, color: "var(--ink-soft)" }}>Couldn't load stats: {error}</div>;
  }
  if (!stats) {
    return <div style={{ padding: 32, color: "var(--ink-soft)" }}>Loading…</div>;
  }

  const remaining = Math.max(0, stats.goal - stats.miles);

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          padding: isDesktop ? "64px 64px 56px" : "24px 22px 32px",
          display: "grid",
          gridTemplateColumns: isDesktop ? "1.1fr 1fr" : "1fr",
          gap: isDesktop ? 56 : 28,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              background: "var(--accent-tint)",
              color: "var(--accent)",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.4,
              marginBottom: 18,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--accent)" }} />
            Live · summer 2026
          </div>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--display)",
              fontWeight: 400,
              fontSize: isDesktop ? "clamp(48px, 5vw, 68px)" : "clamp(36px, 9vw, 44px)",
              lineHeight: 1.02,
              letterSpacing: -1.6,
              color: "var(--ink)",
            }}
          >
            {stats.heroCopy}
          </h1>
          <p style={{ marginTop: 18, fontSize: isDesktop ? 18 : 16, lineHeight: 1.5, color: "var(--ink-soft)", maxWidth: 540, whiteSpace: "pre-wrap" }}>
            {stats.heroSubcopy}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 }}>
            <Btn size="lg" variant="primary" onClick={() => nav("/pledge")}>
              Make a Pledge
            </Btn>
            <Btn size="lg" variant="secondary" onClick={() => nav("/my-pledge")}>
              Check My Pledge
            </Btn>
          </div>
        </div>

        {stats.heroImage ? (
          <img
            src={stats.heroImage}
            alt=""
            style={{
              width: "100%",
              aspectRatio: isDesktop ? "4/5" : "5/4",
              objectFit: "cover",
              borderRadius: 18,
              border: "1px solid var(--line)",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              aspectRatio: isDesktop ? "4/5" : "5/4",
              borderRadius: 18,
              background: "linear-gradient(135deg, var(--accent-tint), var(--surface))",
              border: "1px solid var(--line)",
              backgroundImage: "repeating-linear-gradient(135deg, transparent 0 14px, rgba(0,0,0,0.04) 14px 15px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ink-soft)",
              fontFamily: "ui-monospace, SF Mono, monospace",
              fontSize: 12,
            }}
          >
            [ photo of Scott on the bike ]
          </div>
        )}
      </section>

      {/* Progress card */}
      <section style={{ padding: isDesktop ? "0 64px 24px" : "8px 22px 16px" }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 20, padding: isDesktop ? 32 : 22 }}>
          <Progress miles={stats.miles} goal={stats.goal} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              marginTop: 24,
              paddingTop: 20,
              borderTop: "1px solid var(--line)",
            }}
          >
            <Stat value={money(stats.totalPledged)} label="Pledged" accent />
            <Stat value={stats.pledgerCount} label="Pledgers" />
            <Stat value={stats.daysRidden} label="Days ridden" />
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section style={{ padding: isDesktop ? "40px 64px 8px" : "24px 22px 8px" }}>
        <SectionHead
          eyebrow="The pledger wall"
          title="Who's got Baker's back?"
        />
        <Leaderboard rows={fullWall ?? stats.pledgers} isDesktop={isDesktop} />
      </section>

      {/* Live ride feed */}
      <section style={{ padding: isDesktop ? "48px 64px 8px" : "28px 22px 8px" }}>
        <SectionHead
          eyebrow="Live from Strava"
          title="Recent rides"
          action={<SyncBadge lastSyncAt={stats.lastSyncAt} />}
        />
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(2, 1fr)" : "1fr", gap: 14 }}>
          {stats.rides.map((r) => (
            <RideCard key={r.rideId} ride={r} />
          ))}
          {stats.rides.length === 0 && (
            <div style={{ padding: 28, textAlign: "center", color: "var(--ink-soft)", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16 }}>
              No rides yet — Scott hasn't started (or Strava isn't connected). Check back soon.
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section style={{ padding: isDesktop ? "56px 64px" : "32px 22px 28px" }}>
        <div
          style={{
            background: "var(--ink)",
            color: "var(--bg)",
            borderRadius: 20,
            padding: isDesktop ? "40px 44px" : "28px 24px",
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            alignItems: isDesktop ? "center" : "flex-start",
            justifyContent: "space-between",
            gap: 22,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--display)", fontSize: isDesktop ? 32 : 24, fontWeight: 400, letterSpacing: -0.6 }}>
              {remaining} miles to go.
            </h3>
            <p style={{ margin: "8px 0 0", opacity: 0.7, fontSize: 14, maxWidth: 480 }}>
              Add your name. The bigger the wall, the harder Scott rides.
            </p>
          </div>
          <Btn size="md" variant="primary" onClick={() => nav("/pledge")}>
            Make a Pledge
          </Btn>
        </div>
      </section>
    </div>
  );
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth > 720 : true);
  useEffect(() => {
    const f = () => setIsDesktop(window.innerWidth > 720);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return isDesktop;
}
